/**
 * saas-auth product-admin identity DTOs (TMMPDM-0004).
 *
 * Every shape maps VERBATIM to
 * docs/design/HANDOFF-saas-auth-tenant-detail-invitations.md (§1 detail, §2
 * invitations, §5 create, §6 members). saas-auth wraps every response in the
 * `{success,message,data}` ApiResponse envelope — every call in `./tenants.ts`
 * calls `unwrap()`.
 */

export type TenantStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
export type SignupMode = 'PUBLIC' | 'INVITE_ONLY' | 'ADMIN_CREATED_ONLY' | 'DISABLED';

/** §1 — `GET /tenants/{tenantId}`; also historically the create response (now on tmm-api). */
export interface TenantDto {
  id: number;
  slug: string;
  name: string;
  status: TenantStatus;
  signupMode: SignupMode;
  /** Present and `null` when unset — NEVER omitted (contract §1.1). Nullable, not
   *  optional: the key is always on the wire. TMMPDM-0004 — declaring these `?: string`
   *  made a null-unsafe guard typecheck as exhaustive and crashed the whole Settings tab. */
  emailFromName: string | null;
  emailFromAddress: string | null;
  emailSupportEmail: string | null;
  /** Omitted when null (contract §1). */
  productId?: number;
  brandId?: number;
  planId?: number;
  /** TMMPDM-0015 (SASATH-0335) — NON_NULL: omitted when the tenant has no reseller. An id
   *  only; the console maps it through the reseller list. */
  resellerId?: number;
  createdAt: string;
}

/**
 * Member role enum. `GET …/users` is the customer list (`listMembers`);
 * this console’s Staff tab uses `GET …/staff` (`listStaff`). `GUEST` is
 * roster-only and filtered out of `listMembers` — never rendered here.
 */
export type TenantMemberRole = 'OWNER' | 'SUPER_ADMIN' | 'ADMIN' | 'STAFF' | 'USER' | 'GUEST';

/** Roles assignable via `addMember`/`changeRole` — staff ladder only. Never `USER`. */
export type AssignableRole = 'OWNER' | 'ADMIN' | 'STAFF';

export const STAFF_ROLES = ['OWNER', 'ADMIN', 'STAFF'] as const;

export function isStaffRole(role: string): role is AssignableRole {
  return (STAFF_ROLES as readonly string[]).includes(role);
}

export type TenantMemberStatus = 'ACTIVE' | 'INVITED' | 'DISABLED';

/**
 * One row of `GET /tenants/{tenantId}/staff` (and the unused customer
 * `GET …/users`). Mutations key on `membershipId`, NOT userId.
 */
export interface TenantMemberDto {
  membershipId: number;
  email: string;
  /** saas-auth `@Nullable String` with default Jackson inclusion — on the wire as `null`, never absent. */
  name: string | null;
  role: TenantMemberRole;
  status: TenantMemberStatus;
}

export interface AddMemberRequest {
  email: string;
  role: AssignableRole;
}

/** §2 — invitation status is server-COMPUTED; render as the badge as-is, never recompute it. */
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

/** §2 — one row of `GET /tenants/{tenantId}/invitations` (a JSON array, not paged). */
export interface TenantInvitationDto {
  id: number;
  email: string;
  role: TenantMemberRole;
  oidcClientIds: number[];
  status: InvitationStatus;
  tenantId: number;
  invitedByUserId: number;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
}

/**
 * §6 — `POST /tenants/{tenantId}/users` discriminated result: an existing
 * account is added directly as a member (201), or an unknown email is
 * invited instead (202, appears in the invitations list). See
 * `./tenants.ts#addMember` for how this is discriminated.
 */
export type AddMemberResult =
  | { kind: 'member'; member: TenantMemberDto }
  | { kind: 'invitation'; invitation: TenantInvitationDto };

/* ---------------------------------------------------------------- reseller members (TMMPDM-0015)
 * saas-auth SASATH-0337, read from the Java at `9a08cf8`. Reseller business DTOs moved to
 * `../tmm/types.ts` with TMMPDM-0017 (they now come from tmm-api). Every `@Nullable` here is
 * present-and-`null`.
 */

export type ResellerRole = 'OWNER' | 'ADMIN' | 'STAFF';
export type ResellerMemberStatus = 'ACTIVE' | 'SUSPENDED';

export const RESELLER_ROLES: readonly ResellerRole[] = ['OWNER', 'ADMIN', 'STAFF'];

export interface ResellerMemberDto {
  /** The membership id — the `{memberId}` path segment. */
  id: number;
  userId: number;
  email: string;
  name: string | null;
  role: ResellerRole;
  status: ResellerMemberStatus;
  mfaEnabled: boolean;
  hasPassword: boolean;
  createdAt: string;
}

/** Never carries the token. `status` is server-computed — render, never recompute. */
export interface ResellerInvitationDto {
  id: number;
  email: string;
  role: ResellerRole;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
  invitedByUserId: number | null;
}
